// Resizing a blank map from any side (docs/architecture/maps.md): the cell
// counts a master drafts, the left and top the server shifts the map by, and
// the pure geometry the board's preview and "Cortar bordas vazias" read.
// Everything is in the saved map's cell indexes: the new frame is the cells
// [-left, -left + cols) across and [-top, -top + rows) down, so nothing on the
// map moves, the frame does.
import type { LiveDrawing, LiveNote, LiveScene, LiveToken } from "../types/generated/live";
import { cellCounts, gridMath, sceneSize } from "./grid";
import { paintable } from "./terrain";

export type Counts = { cols: number; rows: number };
export type Side = "start" | "both" | "end";
// What the draft changes: `from` is the saved size, `left` and `top` the cells added on those sides (negative removes).
export type Resize = { from: Counts; to: Counts; left: number; top: number };
type Point = { x: number; y: number };
type Marks = Record<string, unknown> | null | undefined;
export type Contents = {
	terrain?: Marks;
	edges?: Marks;
	fog?: Marks;
	tokens: Pick<LiveToken, "x" | "y" | "size">[];
	drawings: Pick<LiveDrawing, "points">[];
	notes: Pick<LiveNote, "x" | "y">[];
};

// The cells added on the start side (left or top) of a change of `delta` cells; the odd cell of a split goes to the end.
export function startShare(delta: number, side: Side): number {
	if (side === "start") return delta;
	return side === "both" ? Math.trunc(delta / 2) : 0;
}

type Grid = LiveScene["grid"];
export type Size = { width_px: number; height_px: number };
export type Rect = { x: number; y: number; width_px: number; height_px: number };
type Placed = Pick<LiveToken, "x" | "y" | "size">;

// The grid the map has after `left` columns are added at its left: an odd hex shift swaps which columns sit half a row
// lower, so the parity flips and the offset moves half a cell, every cell keeping its pixels (twin of MapScene#shift_records).
export function shiftedGrid(grid: Grid, left: number): Grid {
	if (grid.kind !== "hex" || left % 2 === 0) return grid;
	const flipped = grid.hex_parity === "odd" ? "even" : "odd";
	return { ...grid, hex_parity: flipped, offset_y: grid.offset_y + (flipped === "even" ? grid.cell_h / 2 : -grid.cell_h / 2) };
}

const overlap = (a: Rect, b: Rect): Rect => {
	const x = Math.min(a.x, b.x);
	const y = Math.min(a.y, b.y);
	return { x, y, width_px: Math.max(a.x + a.width_px, b.x + b.width_px) - x, height_px: Math.max(a.y + a.height_px, b.y + b.height_px) - y };
};

// What a resize does to a map of the `saved` size, in the saved map's pixels, as the server will do it (MapScene#resize).
// The new frame starts at (-dx, -dy), since everything on the map moves by (dx, dy) while the cells keep their pixels.
// `inSaved` and `inNew` tell whether a cell of the saved grid is kept by each, by the server's paintable rule; `pulled`
// lists the tokens it brings back, each with the pixel it lands on.
export function previewOf(grid: Grid, saved: Size, { to, left, top }: Resize, was: Size = saved) {
	const next = shiftedGrid(grid, left);
	const dx = left * grid.cell_w * (grid.kind === "hex" ? 0.75 : 1);
	const dy = top * grid.cell_h;
	const size = sceneSize(next, to) as Size;
	const before = gridMath(grid);
	const after = gridMath(next);
	const frame = { x: 0 - dx, y: 0 - dy, ...size };
	const counts = cellCounts(next, size.width_px, size.height_px);
	// Only a shift or a shrink pulls tokens in, a shrink being measured against the size the map has stored (`was`, MapScene#fit_to_size),
	// while `saved` is the size its drafted cell size gives it, which the preview draws.
	const pulls = left !== 0 || top !== 0 || size.width_px < was.width_px || size.height_px < was.height_px;
	return {
		frame,
		reach: overlap({ x: 0, y: 0, ...saved }, frame),
		inSaved: (col: number, row: number) => paintable(before, saved, { col, row }),
		inNew: (col: number, row: number) => paintable(after, size, { col: col + left, row: row + top }),
		pulled<T extends Placed>(tokens: T[]) {
			if (!pulls) return [];
			const found: { token: T; landing: Point }[] = [];
			for (const token of tokens) {
				const x = token.x + dx;
				const y = token.y + dy;
				if (x >= 0 && x <= size.width_px && y >= 0 && y <= size.height_px) continue;
				const cell = after.cellAt(clamp(x, 0, size.width_px), clamp(y, 0, size.height_px));
				const centre = after.centerOf(clamp(cell.col, 0, counts.cols - 1), clamp(cell.row, 0, counts.rows - 1));
				found.push({ token, landing: { x: clamp(centre.x, 0, size.width_px) - dx, y: clamp(centre.y, 0, size.height_px) - dy } });
			}
			return found;
		},
	};
}

function clamp(value: number, low: number, high: number) {
	return Math.min(Math.max(value, low), high);
}

// The smallest frame of the saved size that holds every token, drawing, note and marked cell, or null for an empty map.
// A hex token counts for its centre cell and the ring around it that its size reaches; a square one for the cells it covers.
export function trimmed(grid: LiveScene["grid"], counts: Counts, contents: Contents): { cols: number; rows: number; left: number; top: number } | null {
	const math = gridMath(grid);
	const box = { c0: Number.POSITIVE_INFINITY, c1: Number.NEGATIVE_INFINITY, r0: Number.POSITIVE_INFINITY, r1: Number.NEGATIVE_INFINITY };
	const add = (col: number, row: number) => {
		box.c0 = Math.min(box.c0, col);
		box.c1 = Math.max(box.c1, col);
		box.r0 = Math.min(box.r0, row);
		box.r1 = Math.max(box.r1, row);
	};
	// A token covers the cells under its circle: its centre and the eight points round it, a pixel in.
	for (const { x, y, size } of contents.tokens) {
		const reach = Math.max((Math.max(size, 0) * Math.min(grid.cell_w, grid.cell_h)) / 2 - 1, 0);
		const diagonal = reach * Math.SQRT1_2;
		for (const [dx, dy] of [
			[0, 0],
			[reach, 0],
			[-reach, 0],
			[0, reach],
			[0, -reach],
			[diagonal, diagonal],
			[-diagonal, diagonal],
			[diagonal, -diagonal],
			[-diagonal, -diagonal],
		]) {
			const { col, row } = math.cellAt(x + dx, y + dy);
			add(col, row);
		}
	}
	for (const { points } of contents.drawings) {
		for (let index = 0; index + 1 < points.length; index += 2) {
			const { col, row } = math.cellAt(points[index], points[index + 1]);
			add(col, row);
		}
	}
	for (const { x, y } of contents.notes) {
		const { col, row } = math.cellAt(x, y);
		add(col, row);
	}
	const mark = (lines: unknown, wanted: (letter: string) => boolean, across?: [number, number]) => {
		if (!Array.isArray(lines)) return;
		lines.forEach((line, row) => {
			if (typeof line !== "string") return;
			for (let col = 0; col < line.length; col++) {
				if (!wanted(line[col])) continue;
				add(col, row);
				if (across) add(col + across[0], row + across[1]);
			}
		});
	};
	const painted = (letter: string) => letter !== ".";
	for (const lines of Object.values(contents.terrain ?? {})) mark(lines, painted);
	// An edge between two cells is kept only when both are on the map: n[row][col] is shared with the cell above, w with the one to the left.
	for (const [key, lines] of Object.entries(contents.edges ?? {})) {
		mark(lines, painted, key === "n" ? [0, -1] : key === "w" ? [-1, 0] : undefined);
	}
	mark(contents.fog?.rows, (letter) => letter === "r");
	if (box.c0 > box.c1) return null;
	const c0 = clamp(box.c0, 0, counts.cols - 1);
	const c1 = clamp(box.c1, 0, counts.cols - 1);
	const r0 = clamp(box.r0, 0, counts.rows - 1);
	const r1 = clamp(box.r1, 0, counts.rows - 1);
	return { cols: c1 - c0 + 1, rows: r1 - r0 + 1, left: 0 - c0, top: 0 - r0 };
}
